import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Eye, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { storesService } from '../services/stores';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const StoresPreview = () => {
  const navigate = useNavigate();
  const [selectedStore, setSelectedStore] = useState(null);
  const [participatingStores, setParticipatingStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const stores = await storesService.getParticipatingStores();
        setParticipatingStores(stores);
        setError(null);
      } catch (err) {
        console.error('Error loading stores:', err);
        setError('Error al cargar las tiendas');
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  return (
    <div className="min-h-screen bg-black px-6 py-8">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8">
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white hover:text-[#A2A2A2] transition-colors mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver</span>
        </button>
        
        <h1 className="text-4xl font-bold text-white mb-3">
          Tiendas participantes
        </h1>
        <p className="text-[#A2A2A2] text-lg">
          Estas son algunas de las tiendas donde puedes usar tu membresía
        </p>
      </div>

      {/* Stores Grid */}
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && (
          <div className="col-span-2 text-center text-white py-8">
            Cargando tiendas...
          </div>
        )}
        {error && (
          <div className="col-span-2 text-center text-red-400 py-8">
            {error}
          </div>
        )}
        {!loading && !error && participatingStores.length === 0 && (
          <div className="col-span-2 text-center text-[#A2A2A2] py-8">
            No hay tiendas disponibles
          </div>
        )}
        {!loading && !error && participatingStores.map((store) => (
          <div
            key={store.id}
            onClick={() => setSelectedStore(store)}
            className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/30 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
                  {store.name} <Eye className="w-4 h-4 text-white" />
                </h3>
                <p className="text-[#A2A2A2] text-sm">{store.category}</p>
              </div>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
              {store.benefits && store.benefits.length > 0 ? (
                <div className="space-y-2">
                  {store.benefits.map((benefit, index) => (
                    <div key={benefit.id || index} className="flex items-start gap-2">
                      <Gift className="w-4 h-4 text-[#F4A261] mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{benefit.title}</p>
                        {benefit.description && (
                          <p className="text-[#A2A2A2] text-xs mt-1">{benefit.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A2A2A2] text-sm">Sin beneficios disponibles</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto mt-12 text-center">
        <Button
          onClick={() => navigate('/signup')}
          className="bg-[#24b183] text-white hover:bg-[#1f9a72] rounded-full px-8 py-6 text-lg font-semibold transition-all hover:scale-105 active:scale-95 shadow-lg"
        >
            Únete a CCE Puebla
        </Button>
      </div>

      {/* Store Detail Dialog */}
      <Dialog open={!!selectedStore} onOpenChange={() => setSelectedStore(null)}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{selectedStore?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Categoría</p>
              <p className="text-white">{selectedStore?.category}</p>
            </div>
            
            <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Beneficios</p>
              {selectedStore?.benefits && selectedStore.benefits.length > 0 ? (
                <div className="space-y-3">
                  {selectedStore.benefits.map((benefit, index) => (
                    <div key={benefit.id || index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-start gap-3">
                        <Gift className="w-5 h-5 text-[#F4A261] mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-white font-medium text-base">{benefit.title}</p>
                            {!benefit.isActive && (
                              <span className="text-xs bg-gray-500/20 text-gray-300 px-2 py-0.5 rounded-full">
                                Inactivo
                              </span>
                            )}
                          </div>
                          {benefit.description && (
                            <p className="text-[#A2A2A2] text-sm">{benefit.description}</p>
                          )}
                          {benefit.validUntil && (
                            <p className="text-[#A2A2A2] text-xs mt-2">
                              Válido hasta: {new Date(benefit.validUntil).toLocaleDateString('es-MX', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#A2A2A2] text-sm">Sin beneficios disponibles</p>
              )}
            </div>

            {/* <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Descripción</p>
              <p className="text-white">{selectedStore?.description}</p>
            </div> */}

            {/* <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Vigencia</p>
              <p className="text-white">Hasta {selectedStore?.validUntil}</p>
            </div> */}

            {/* Leyenda de uso del beneficio */}
            {selectedStore?.benefits && selectedStore.benefits.length > 0 && (
              <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                <p className="text-[#A2A2A2] text-xs leading-relaxed text-center">
                  Muestra tu QR en la tienda para aplicar el beneficio.<br />
                  Consulta términos y condiciones en tienda. Exclusivo para miembros CCE Puebla
                </p>
              </div>
            )}

            <Button
              onClick={() => {
                setSelectedStore(null);
                navigate('/signup');
              }}
              className="w-full bg-[#24b183] text-white hover:bg-[#1f9a72] rounded-full py-6 mt-4 transition-all hover:scale-105 active:scale-95 font-semibold shadow-lg"
            >
              Activar membresía
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoresPreview;
